import { useState, useCallback, useMemo, useRef, useEffect } from "react";
import { MapPin, Search, X, Clock, ArrowLeft, Check } from "lucide-react";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { cn } from "@/lib/utils";
import { useIsMobile } from "@/hooks/use-mobile";
import { useTranslation } from "react-i18next";
import { cityMatchesQuery, getLocalizedCityName } from "@/lib/i18n/cityNames";

interface LocationSelectorProps {
  value: string;
  onSelect: (city: string) => void;
  onClear: () => void;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  popularCities: string[];
  allCities: string[];
  recentSearches: string[];
  compact?: boolean;
  /** Shared pill segment styles from SearchBar (hero / full-width bar) */
  segmentClassName?: string;
  t: {
    where: string;
    anywhere: string;
    placeholder: string;
    recentSearches: string;
    popularChoices: string;
    results: string;
    noResults: (query: string) => string;
    clearLabel?: string;
    noCityAvailable?: string;
  };
}

export const LocationSelector = ({
  value,
  onSelect,
  onClear,
  open,
  onOpenChange,
  popularCities,
  allCities,
  recentSearches,
  compact = false,
  segmentClassName,
  t,
}: LocationSelectorProps) => {
  const { t: tI18n } = useTranslation();
  const isMobile = useIsMobile();
  const [searchInput, setSearchInput] = useState("");
  const inputRef = useRef<HTMLInputElement | null>(null);

  const hasSearch = searchInput.trim() !== "";

  const filteredCities = useMemo(() => {
    if (!hasSearch) return [];
    const searchLower = searchInput.trim();
    return allCities
      .filter((city) => cityMatchesQuery(city, searchLower, tI18n))
      .slice(0, 15);
  }, [searchInput, allCities, hasSearch, tI18n]);

  const scrollableCities = useMemo(() => {
    if (hasSearch) return filteredCities;
    return allCities.filter((city) => !popularCities.includes(city));
  }, [hasSearch, filteredCities, allCities, popularCities]);

  const handleSelect = useCallback(
    (city: string) => {
      onSelect(city);
      setSearchInput("");
    },
    [onSelect]
  );

  const handleOpenChange = useCallback(
    (newOpen: boolean) => {
      if (!newOpen) setSearchInput("");
      onOpenChange(newOpen);
    },
    [onOpenChange]
  );

  useEffect(() => {
    if (open && inputRef.current) {
      const timer = setTimeout(() => inputRef.current?.focus(), 150);
      return () => clearTimeout(timer);
    }
  }, [open]);

  const triggerRowClass = cn(
    "flex w-full min-w-0 items-center",
    compact
      ? "gap-2 rounded-lg px-2.5 py-2 transition-colors hover:bg-muted focus-within:ring-2 focus-within:ring-ring focus-within:ring-offset-2"
      : segmentClassName ??
        "gap-3 rounded-2xl px-4 py-3 transition-colors duration-200 hover:bg-neutral-50/90 focus-within:ring-2 focus-within:ring-green-night/25 focus-within:ring-offset-2",
    !compact && open && "bg-neutral-50 ring-2 ring-green-night/15"
  );

  const triggerMain = (
    <>
      <MapPin
        className={cn(
          "shrink-0",
          compact ? "h-3.5 w-3.5" : "h-5 w-5",
          value ? "text-text-tertiary" : "text-neutral-400"
        )}
        aria-hidden
      />
      <div className="min-w-0 flex-1 text-start">
        {!compact && (
          <p className="text-xs font-medium leading-none text-neutral-500">
            {t.where}
          </p>
        )}
        {compact && (
          <p className="hidden text-[11px] font-medium leading-none text-text-tertiary sm:block">
            {t.where}
          </p>
        )}
        <p
          className={cn(
            "truncate font-medium leading-snug",
            compact ? "text-sm text-text-primary" : "mt-1 text-[15px] font-semibold",
            !compact && (value ? "text-neutral-900" : "text-neutral-500"),
            compact && !value && "text-text-secondary"
          )}
        >
          {value ? getLocalizedCityName(value, tI18n) : t.anywhere}
        </p>
      </div>
    </>
  );

  const clearCityButton = value && !compact ? (
    <button
      type="button"
      onClick={(e) => {
        e.stopPropagation();
        e.preventDefault();
        onClear();
      }}
      className="ms-1 shrink-0 rounded-full p-1.5 text-neutral-400 transition-colors hover:bg-neutral-100 hover:text-neutral-600"
      aria-label={tI18n("common.ariaClearCity")}
    >
      <X className="h-4 w-4" aria-hidden />
    </button>
  ) : null;

  const SearchInput = ({ mobile = false }: { mobile?: boolean }) => (
    <div
      className={cn(
        "flex-shrink-0",
        mobile
          ? "sticky top-0 z-10 bg-white px-4 py-3 border-b border-gray-100"
          : "px-3 pt-3 pb-2"
      )}
    >
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
        <input
          ref={inputRef}
          type="text"
          inputMode="search"
          autoFocus
          placeholder={t.placeholder}
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          className={cn(
            "w-full pl-10 pr-9 rounded-xl text-sm",
            "bg-gray-50 border border-gray-200",
            "focus:outline-none focus:border-green-night/40 focus:bg-white focus:ring-1 focus:ring-green-night/10",
            "transition-all duration-200 placeholder:text-gray-400",
            mobile ? "py-3 text-[16px]" : "py-2.5"
          )}
          aria-label={t.placeholder}
        />
        {searchInput && (
          <button
            type="button"
            onClick={() => setSearchInput("")}
            className="absolute right-2.5 top-1/2 -translate-y-1/2 p-1 hover:bg-gray-200 rounded-full transition-colors"
            aria-label={t.clearLabel ?? "Clear search"}
          >
            <X className="w-3.5 h-3.5 text-gray-500" />
          </button>
        )}
      </div>
    </div>
  );

  const MobileCityList = (
    <div className="px-4 py-2 min-h-[100px]">
      {recentSearches.length > 0 && !searchInput && (
        <>
          <div className="flex items-center gap-2 px-1 py-2 mb-1">
            <Clock className="w-3.5 h-3.5 text-gray-400" />
            <span className="text-xs font-medium text-gray-400 uppercase tracking-wider">
              {t.recentSearches}
            </span>
          </div>
          <div className="space-y-0.5 mb-3">
            {recentSearches.map((city) => (
              <CityRow
                key={`recent-${city}`}
                city={city}
                onClick={() => handleSelect(city)}
                variant="recent"
                isSelected={city === value}
                isMobile
              />
            ))}
          </div>
          <div className="h-px bg-gray-100 mb-3" />
        </>
      )}
      <div className="flex items-center gap-2 px-1 py-2 mb-1">
        {searchInput ? (
          <span className="text-xs font-medium text-gray-400 uppercase tracking-wider">
            {t.results}
            {filteredCities.length > 0 && (
              <span className="ml-1.5 text-gray-500">
                ({filteredCities.length})
              </span>
            )}
          </span>
        ) : (
          <span className="text-xs font-medium text-gray-400 uppercase tracking-wider">
            {t.popularChoices}
          </span>
        )}
      </div>
      {(searchInput ? filteredCities : popularCities).length > 0 ? (
        <div className="space-y-0.5">
          {(searchInput ? filteredCities : popularCities).map((city, index) => (
            <CityRow
              key={`${city}-${index}`}
              city={city}
              onClick={() => handleSelect(city)}
              variant="popular"
              isSelected={city === value}
              isMobile
            />
          ))}
        </div>
      ) : (
        <EmptyState
          message={
            searchInput
              ? t.noResults(searchInput)
              : (t.noCityAvailable ?? "No city available")
          }
        />
      )}
    </div>
  );

  if (isMobile) {
    return (
      <>
        <div className={cn("w-full min-w-0", compact && "flex-1", triggerRowClass)}>
          <button
            type="button"
            aria-label={t.where}
            aria-expanded={open}
            aria-haspopup="dialog"
            onClick={() => handleOpenChange(true)}
            className={cn(
              "flex min-w-0 flex-1 items-center bg-transparent text-start",
              compact ? "gap-2" : "gap-2.5"
            )}
          >
            {triggerMain}
          </button>
          {clearCityButton}
        </div>
        <Dialog open={open} onOpenChange={handleOpenChange}>
          <DialogContent
            className={cn(
              "max-w-full h-full max-h-screen m-0 p-0 rounded-none",
              "flex flex-col overflow-hidden bg-white dark:bg-gray-800",
              "left-0 top-0 translate-x-0 translate-y-0",
              "w-full h-full sm:rounded-none",
              "[&>button]:hidden"
            )}
          >
            <DialogHeader className="flex-shrink-0 px-4 py-3 border-b border-gray-100 bg-white">
              <div className="flex items-center gap-3">
                <button
                  type="button"
                  onClick={() => handleOpenChange(false)}
                  className="p-2 -ml-2 hover:bg-gray-100 rounded-full transition-colors"
                  aria-label={tI18n('common.ariaClose')}
                >
                  <ArrowLeft className="w-5 h-5 text-gray-600" />
                </button>
                <DialogTitle className="text-lg font-semibold text-gray-900">
                  {t.where}
                </DialogTitle>
              </div>
            </DialogHeader>
            <SearchInput mobile />
            <div className="flex-1 min-h-0 overflow-y-auto">
              {MobileCityList}
            </div>
          </DialogContent>
        </Dialog>
      </>
    );
  }

  return (
    <div className={cn("w-full min-w-0", compact && "flex-1")}>
    <Popover open={open} onOpenChange={handleOpenChange}>
      <div className={triggerRowClass}>
        <PopoverTrigger asChild>
          <button
            type="button"
            aria-label={t.where}
            aria-expanded={open}
            aria-haspopup="dialog"
            className={cn(
              "flex min-w-0 flex-1 items-center bg-transparent text-start",
              compact ? "gap-2" : "gap-2.5"
            )}
          >
            {triggerMain}
          </button>
        </PopoverTrigger>
        {clearCityButton}
      </div>
      <PopoverContent
        className={cn(
          "w-[min(420px,var(--radix-popover-content-available-width))] p-0 rounded-2xl overflow-hidden",
          "z-50 border border-border bg-white shadow-lg dark:border-gray-700 dark:bg-gray-800"
        )}
        side="bottom"
        align="start"
        sideOffset={6}
        avoidCollisions={false}
      >
        <SearchInput />

        {/* Popular cities as compact pills */}
        {!hasSearch && popularCities.length > 0 && (
          <div className="flex-shrink-0 px-3 pb-3">
            <p className="mb-2 text-xs font-medium text-text-tertiary">
              {t.popularChoices}
            </p>
            <div className="flex flex-wrap gap-1.5">
              {popularCities.map((city) => (
                <button
                  key={`popular-${city}`}
                  type="button"
                  onClick={() => handleSelect(city)}
                  className={cn(
                    "inline-flex items-center rounded-full border px-3 py-1.5 text-sm transition-colors",
                    city === value
                      ? "border-green-night bg-scandi-bg font-medium text-text-primary"
                      : "border-border/70 text-text-secondary hover:border-border hover:text-text-primary"
                  )}
                  aria-selected={city === value}
                >
                  {getLocalizedCityName(city, tI18n)}
                </button>
              ))}
            </div>
          </div>
        )}

        {!hasSearch && <div className="mx-3 h-px bg-border/70" />}

        <div>
          {/* Recent searches */}
          {recentSearches.length > 0 && !hasSearch && (
            <div className="px-3 pt-2.5 pb-1">
              <div className="flex items-center gap-1.5 mb-2">
                <Clock className="w-3 h-3 text-gray-400" />
                <span className="text-xs font-medium text-text-tertiary">
                  {t.recentSearches}
                </span>
              </div>
              <div className="space-y-0.5">
                {recentSearches.map((city) => (
                  <CityRow
                    key={`recent-${city}`}
                    city={city}
                    onClick={() => handleSelect(city)}
                    variant="recent"
                    isSelected={city === value}
                  />
                ))}
              </div>
              <div className="h-px bg-gray-100 mt-2.5" />
            </div>
          )}

          {/* Search results header */}
          {hasSearch && (
            <div className="px-3 pt-2.5 pb-1">
              <span className="text-xs font-medium text-text-tertiary">
                {t.results}
                {filteredCities.length > 0 && (
                  <span className="ml-1 text-gray-500">
                    ({filteredCities.length})
                  </span>
                )}
              </span>
            </div>
          )}

          {/* All other cities header */}
          {!hasSearch && scrollableCities.length > 0 && (
            <div className="px-3 pb-3 pt-2.5">
              <p className="mb-2 text-xs font-medium text-text-tertiary">
                {tI18n("search.allCities")}
              </p>
              <div className="flex flex-wrap gap-1.5">
                {scrollableCities.map((city) => (
                  <button
                    key={`city-${city}`}
                    type="button"
                    onClick={() => handleSelect(city)}
                    className={cn(
                      "inline-flex items-center rounded-full border px-3 py-1.5 text-sm transition-colors",
                      city === value
                        ? "border-green-night bg-scandi-bg font-medium text-text-primary"
                        : "border-border/70 text-text-secondary hover:border-border hover:text-text-primary"
                    )}
                    aria-selected={city === value}
                  >
                    {getLocalizedCityName(city, tI18n)}
                  </button>
                ))}
              </div>
            </div>
          )}

          <div className="px-3 pb-2">
            {hasSearch ? (
              filteredCities.length > 0 ? (
                <div className="space-y-0.5">
                  {filteredCities.map((city, index) => (
                    <CityRow
                      key={`search-${city}-${index}`}
                      city={city}
                      onClick={() => handleSelect(city)}
                      variant="popular"
                      isSelected={city === value}
                    />
                  ))}
                </div>
              ) : (
                <EmptyState message={t.noResults(searchInput)} />
              )
            ) : scrollableCities.length > 0 ? null : (
              <EmptyState message={t.noCityAvailable ?? "No city available"} />
            )}
          </div>
        </div>
      </PopoverContent>
    </Popover>
    </div>
  );
};

interface CityRowProps {
  city: string;
  onClick: () => void;
  variant: "recent" | "popular";
  isSelected?: boolean;
  isMobile?: boolean;
}

const CityRow = ({
  city,
  onClick,
  variant,
  isSelected = false,
  isMobile = false,
}: CityRowProps) => {
  const { t } = useTranslation();
  const label = getLocalizedCityName(city, t);
  return (
  <button
    type="button"
    onClick={onClick}
    className={cn(
      "w-full flex items-center gap-2.5 rounded-lg text-left group transition-colors duration-150",
      isMobile ? "px-3 py-3 active:bg-gray-50" : "px-2.5 py-2 hover:bg-gray-50",
      isSelected && "bg-scandi-bg"
    )}
    aria-selected={isSelected}
  >
    <div
      className={cn(
        "flex items-center justify-center flex-shrink-0 rounded-full",
        isMobile ? "w-9 h-9" : "w-7 h-7",
        "bg-scandi-bg text-text-tertiary"
      )}
    >
      {variant === "recent" ? (
        <Clock className={isMobile ? "w-4 h-4" : "w-3.5 h-3.5"} />
      ) : (
        <MapPin className={isMobile ? "w-4 h-4" : "w-3.5 h-3.5"} />
      )}
    </div>
    <span
      className={cn(
        "flex-1 transition-colors duration-150",
        isMobile ? "text-[15px]" : "text-sm",
        isSelected
          ? "font-semibold text-text-primary"
          : "font-medium text-text-secondary group-hover:text-text-primary"
      )}
    >
      {label}
    </span>
    {isSelected && (
      <Check
        className={cn(
          "flex-shrink-0 text-green-night",
          isMobile ? "w-4 h-4" : "w-3.5 h-3.5"
        )}
      />
    )}
  </button>
  );
};

const EmptyState = ({ message }: { message: string }) => (
  <div className="py-10 text-center">
    <MapPin className="w-8 h-8 text-gray-300 mx-auto mb-2" />
    <p className="text-gray-400 text-sm">{message}</p>
  </div>
);

export default LocationSelector;
